import { useSearchParams } from "react-router-dom";
import { MapPin, Phone, Mail, Clock } from "lucide-react";
import Seo from "@/components/Seo";
import EnquiryForm from "@/components/EnquiryForm";
import { FadeIn, Eyebrow } from "@/components/Reveal";
import { COMPANY, INSURANCE_TYPES } from "@/data/siteData";
import { useLanguage } from "@/i18n/LanguageContext";

export default function Contact() {
  const [params] = useSearchParams();
  const { t } = useLanguage();
  const rawType = params.get("type") || "";
  const defaultType = INSURANCE_TYPES.includes(rawType) ? rawType : "";

  const HOURS = [
    { days: t("ct.h1d"), time: "9:30 AM – 6:30 PM" },
    { days: t("ct.h2d"), time: "10:00 AM – 2:00 PM" },
    { days: t("ct.h3d"), time: t("ct.closed") },
  ];

  return (
    <div data-testid="contact-page">
      <Seo
        title="Contact Us — Get a Quote | Wisdom Insurance Broking Pune"
        description="Request a commercial insurance quote or talk to an advisor. Wisdom Insurance Broking Private Limited, Pune. Enquiry form, phone, email and office location."
        keywords="insurance broker contact Pune, commercial insurance quote, insurance enquiry India"
      />

      <section className="grain bg-navy-deep pb-16 pt-32 md:pb-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
          <FadeIn>
            <Eyebrow light>{t("ct.eyebrow")}</Eyebrow>
            <h1 className="mt-4 max-w-3xl font-heading text-4xl font-extrabold tracking-tight text-white sm:text-5xl">
              {t("ct.title")}
            </h1>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-slate-300 sm:text-lg">
              {t("ct.sub")}
            </p>
          </FadeIn>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-8 md:py-20 lg:px-16">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
          <FadeIn className="lg:col-span-7">
            <div className="border border-slate-200 bg-white p-6 sm:p-10">
              <h2 className="font-heading text-xl font-semibold text-navy">{t("ct.formTitle")}</h2>
              <p className="mb-8 mt-2 text-sm text-slate-500">{t("ct.formSub")}</p>
              <EnquiryForm key={defaultType} defaultType={defaultType} />
            </div>
          </FadeIn>

          <FadeIn delay={0.15} className="lg:col-span-5">
            <div className="space-y-6">
              <div className="border border-slate-200 bg-white p-8">
                <h3 className="flex items-center gap-2 font-heading text-base font-semibold text-navy">
                  <MapPin className="h-5 w-5 text-amber-600" /> {t("ct.office")}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-slate-600" data-testid="contact-address">
                  {COMPANY.name}<br />
                  {COMPANY.addressLines.map((l) => (
                    <span key={l}>{l}<br /></span>
                  ))}
                </p>
              </div>

              <div className="border border-slate-200 bg-white p-8">
                <h3 className="flex items-center gap-2 font-heading text-base font-semibold text-navy">
                  <Phone className="h-5 w-5 text-amber-600" /> {t("ct.phoneEmail")}
                </h3>
                <p className="mt-3 text-sm text-slate-600" data-testid="contact-phone">{t("ct.phoneL")}: {COMPANY.phone}</p>
                <p className="mt-1 text-sm text-slate-600" data-testid="contact-whatsapp">{t("ct.whatsappL")}: {COMPANY.whatsappDisplay}</p>
                <p className="mt-1 text-sm text-slate-600" data-testid="contact-email">{t("ct.emailL")}: {COMPANY.email}</p>
              </div>

              <div className="border border-slate-200 bg-white p-8">
                <h3 className="flex items-center gap-2 font-heading text-base font-semibold text-navy">
                  <Clock className="h-5 w-5 text-amber-600" /> {t("ct.hours")}
                </h3>
                <ul className="mt-3 space-y-2 text-sm text-slate-600" data-testid="contact-hours">
                  {HOURS.map((h) => (
                    <li key={h.days} className="flex justify-between gap-4">
                      <span>{h.days}</span>
                      <span className="font-medium text-navy">{h.time}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </FadeIn>
        </div>

        <FadeIn className="mt-12">
          <div className="overflow-hidden border border-slate-200" data-testid="contact-map">
            <iframe
              title="Wisdom Insurance Broking — Pune office map"
              src="https://www.google.com/maps?q=Sacred+World,+Wanowrie,+Pune+411040&output=embed"
              className="h-96 w-full"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
          <p className="mt-3 font-mono text-[11px] uppercase tracking-widest text-slate-400">
            {t("ct.mapNote")}
          </p>
        </FadeIn>
      </section>
    </div>
  );
}
